import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import axios from 'axios';
import { auth } from './firebase';

import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import UploadCSV from './components/UploadCSV';
import CustomBehaviors from './pages/CustomBehaviors';
import AudienceIntrest from './pages/AudienceIntrest';
import Insights from './pages/Insights';
import BehaviorsContentSignals from './pages/BehaviorsContentSignals';
import TopicInsights from './components/TopicInsights';
import CsvDashboard from './components/CsvDashboard';
import TopicInsightsRealTime from './components/TopicInsightsRealTime'
import TagInspector from './pages/TagInspector';
import KeywordSearch from './pages/KeywordSearch';
import InspectorHub from './pages/InspectorHub';
import FrameIQCreate from './pages/FrameIQCreate';
import FrameIQList from './pages/FrameIQList';
import FrameIQDownload from './pages/FrameIQDownload';

// ← NEW: YouTube Scrapper pages (now labeled "Campaign IQ" in the sidebar)
import YTCampaignCreate from './pages/YTCampaignCreate';
import YTCampaignList from './pages/YTCampaignList';
import YTCampaignDownload from './pages/YTCampaignDownload';

// ← NEW: Frame IQ — the actual AI video-analysis product
import VideoIntelligenceAnalyze from './pages/VideoIntelligenceAnalyze';
import FrameIQShowcase from './pages/FrameIQShowcase';

const BASE_URL = process.env.REACT_APP_API_URL;

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    // Grab the token BEFORE signOut() — auth.currentUser is gone right after.
    // This tells the backend to revoke the account's Firebase refresh
    // tokens, so this session's ID token (and any copy of it, e.g. in
    // Postman) stops working immediately instead of staying valid for up
    // to its natural 1-hour lifetime. If this call fails for any reason
    // (network blip, etc.) we still proceed with local sign-out below —
    // we never want a failed backend call to trap someone on the dashboard.
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken();
        await axios.post(
          `${BASE_URL}/video_intelligence/api/logout`,
          {},
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
      }
    } catch (err) {
      console.warn('Server-side token revocation failed, logging out locally anyway:', err);
    }

    await signOut(auth);
    setUser(null);

    // Router only wraps the authenticated tree below, so simply clearing
    // `user` swaps the rendered content back to <Login /> without ever
    // touching browser history — the address bar stays on whatever
    // protected path you were on. A full reload back to "/" fixes that
    // and, as a bonus, guarantees no leftover React state survives logout.
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!user) return <Login />;

  return (
    <Router>
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {/* Header */}
        <Header user={user} onLogout={handleLogout} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <div className="flex flex-1 pt-16">
          {/* Sidebar */}
          <Sidebar
            isOpen={sidebarOpen}
            toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            userEmail={user.email}
          />

          {/* Page Content */}
          <main className="flex-1 p-4 mt-16 lg:mt-0">
            <Routes>
              {/* ── Existing routes — untouched ── */}
              <Route path="/dashboard" element={<Dashboard user={user} />} />
              <Route path="/upload-csv" element={<UploadCSV />} />
              <Route path="/custom-behavior" element={<CustomBehaviors />} />
              <Route path="/audienceintrest" element={<AudienceIntrest />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/topic-insights" element={<TopicInsights />} />
              <Route path="/behaviors" element={<BehaviorsContentSignals />} />
              <Route path="/csv-dashboard" element={<CsvDashboard user={user} />} />
              <Route path="/topic-insights-realtime" element={<TopicInsightsRealTime />} />
              <Route path="/tag-inspector" element={<TagInspector />} />
              <Route path="/keyword-search" element={<KeywordSearch />} />
              <Route path="/inspector" element={<InspectorHub />} />
              <Route path='/campaign/create' element={<FrameIQCreate />} />
              <Route path='/campaign/list' element={<FrameIQList />} />
              <Route path='/campaign/download' element={<FrameIQDownload />} />

              {/* ── NEW: YouTube Scrapper routes ── */}
              <Route path='/yt/campaign/create' element={<YTCampaignCreate />} />
              <Route path='/yt/campaign/list' element={<YTCampaignList />} />
              <Route path='/yt/campaign/download' element={<YTCampaignDownload />} />

              {/* ← NEW: Frame IQ — AI video-analysis product */}
              <Route path='/frame-iq' element={<VideoIntelligenceAnalyze />} />
              <Route path='/frame-iq/showcase' element={<FrameIQShowcase />} />

              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </main>
        </div>

        <div className="footer-cont text-center p-4 text-sm text-gray-500">
          © 2025 THEENGAGE.AI – <a href="/privacy-policy" className="underline">Privacy Policy</a> – <a href="/disclaimer" className="underline">Disclaimer</a>
        </div>
      </div>
    </Router>
  );
};

export default App;